<template>
  <div
    id="control-search"
    ref="search"
    :class="['control-search', { 'explore-card': isExploreEnabled }]"
    data-test="Control-Search"
  >
    <!-- Search Bar -->
    <search-bar
      class="desktop-search-bar"
      :text-input="queryInput"
      :placeholder="placeholder"
      role="combobox"
      aria-owns="results-panel"
      aria-haspopup="listbox"
      :aria-expanded="searchIsActive"
      data-test="search-bar"
      @submit-search-clicked="handleSearchSubmitClicked"
      @input-keyup="handleInputKeyup"
      @search-focus="handleSearchFocus"
      @reset-search="handleSearchBarReset"
    />

    <button
      v-if="mobileTestbedActive"
      class="mobile-search-button"
      @click="toggleMobileSearch"
    >
      <x-icon
        icon="magnifying-glass"
        width="20px"
        height="20px"
        color="white"
      />
    </button>

    <div
      v-show="!isExploreEnabled && searchIsActive"
      id="results-panel"
      class="results-panel"
      :class="{ searchCardCollapsed }"
      :aria-hidden="!searchIsActive"
    >
      <div id="results-list" ref="results" class="results-list">
        <div class="mobile-search-header">
          <mobile-prototype-card-handle @click="toggleSearchCardHeight" />
          <div v-if="mobileTestbedActive" class="mobile-search-bar-container">
            <!-- eslint-disable vuejs-accessibility/no-autofocus -->
            <search-bar
              ref="mobileSearchBar"
              class="mobile-search-bar"
              :text-input="queryInput"
              :placeholder="placeholder"
              :autofocus="true"
              role="combobox"
              aria-owns="results-panel"
              aria-haspopup="listbox"
              :aria-expanded="searchIsActive"
              @submit-search-clicked="handleSearchSubmitClicked"
              @input-keyup="handleInputKeyup"
              @search-focus="handleSearchFocus"
              @reset-search="clearSearchData"
            />
            <!-- eslint-enable vuejs-accessibility/no-autofocus -->
            <ys-text
              role="button"
              type="body1"
              class="mobile-search-cancel"
              @click="resetSearch"
            >
              Cancel
            </ys-text>
          </div>

          <!-- Search Segment -->
          <x-segment-control
            class="segmented-control"
            :segments="searchModes"
            appearance-size="full-width"
            @change="handleHuntSegmentChange"
          />
        </div>

        <template v-if="isExamplesVisible || isHistoryVisible">
          <!-- Quick Search -->
          <quick-search
            v-if="showQuickSearch"
            :title="quickSearchConfig?.title"
            :options="quickSearchOptions"
            data-test="quick-search"
            @option-clicked="handleQuickSearchClicked"
          />

          <!-- Examples -->
          <search-examples
            v-if="isExamplesVisible"
            data-test="search-examples"
            @search-example-selected="handleExampleSearchClick"
          />

          <!-- History -->
          <search-history
            v-if="isHistoryVisible"
            data-test="search-history"
            @search-history-selected="handleHistoryItemClick"
          />

          <div
            v-if="searchDiscoverCtaEnabled"
            class="discover-cta-wrapper"
            data-test="search-discover-cta"
          >
            <search-discover-cta />
          </div>
        </template>

        <!-- Error message -->
        <div v-if="error" class="result-error" data-test="search-error">
          {{ error }}
        </div>

        <!-- results -->
        <div class="search-results">
          <!-- Autocomplete Results -->
          <autocomplete-results-list
            v-if="autocompleteResults.length"
            :results="autocompleteResults"
            @result-selected="handleAutocompleteResultSelected"
          />

          <template v-else>
            <!-- State Restrictions -->
            <div
              v-if="landownerSearchStateRestriction"
              class="state-restriction"
            >
              {{ landownerSearchStateRestriction }}
            </div>

            <!-- Grouping -->
            <x-chip-menu
              v-if="isSearchGroupingEnabled && searchResults?.length"
              :chips="chips"
              :all-chip="true"
              :scroll="true"
              data-test="search-results-chip-menu"
              class="search-results-chip-menu"
              @click="handleSearchGroupClick"
            />

            <!-- Loader -->
            <x-loading-spinner
              v-if="searchIsLoading"
              class="loading-spinner"
              data-test="search-loading"
            />

            <!-- Result Label -->
            <ys-text
              v-if="totalResults"
              type="body1"
              class="results-count"
              data-test="search-results-label"
            >
              {{ totalResultsLabel }}
            </ys-text>

            <!-- Result List -->
            <results-list
              v-if="searchResults?.length"
              :search-results="searchResults"
              :show-all="true"
              data-test="search-results-list"
              @result-selected="handleResultSelected"
            />

            <!-- No Results -->
            <template v-if="noResults">
              <ys-text type="body1" class="no-results" data-test="no-results">
                No Results Found
              </ys-text>

              <div
                v-if="searchDiscoverCtaEnabled"
                class="no-results-cta"
                data-test="no-result-cta"
              >
                <div class="discover-cta-wrapper">
                  <search-discover-cta :show-description="true" />
                </div>
              </div>
            </template>
          </template>
        </div>
      </div>
      <footer class="footer">
        <x-metadata-text
          v-if="isMapboxAttributionNeeded"
          weight="bold"
          data-test="mapbox-attribution"
        >
          <span class="mapbox-attribution">
            Geocode powered by
            <x-icon class="mapbox-icon" icon="mapbox" />
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              @click.stop
            >
              OpenStreetMap
            </a>
          </span>
        </x-metadata-text>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
// Libs
import membershipNames from '@/lib/membership-names'
import { SEARCH_ORIGINS } from '@/lib/enums-marketing'
import { getCountWithLabel } from '@/lib/display-formatting'
import {
  isMobileTestbedActive,
  isExploreEnabled,
} from '@/lib/prototyping-flags'
import { isQuickSearchEnabled, getQuickSearchConfig } from '@/lib/quick-search'
import pubsub from '@/lib/pubsub'
import { isSearchAutocompleteEnabled } from '@/lib/search/search-autocomplete'

// Services
import { SearchMode } from '@/services/search-service/constants'
import {
  isLoading,
  searchMode as searchServiceMode,
  setSearchMode,
  setSearchOrigin,
  searchHistory as history,
  searchIsActive as searchServiceActive,
  queryInput,
  searchCompleted,
  lastQueryInputFetchedLocationSearch as searchLastLocationInput,
  lastQueryInputFetchedLandownerSearch as searchLastLandownerInput,
  activeLastSearchTerm,
  totalLocationResults as searchTotalLocationResults,
  totalLandownerResults as searchTotalLandownerResults,
} from '@/services/search-service'

import subscriptionService from '@/services/subscription-service'
import marketingEventService from '@/services/marketing-event-service'
import { useHasAccess } from '@/services/feature-access/composables'
import { DomainFeatureGates } from '@/services/feature-access/global'

// Components
import '@onxmaps/yellowstone-ui/ys-text'
import xIcon from '@/components/shared/x-icon/index.vue'
import xLoadingSpinner from '@/components/shared/x-loading-spinner/index.vue'
import xSegmentControl from '@/components/shared/x-segment-control.vue'
import xChipMenu from '@/components/shared/x-chip-menu/index.vue'
import xMetadataText from '@/components/shared/x-metadata-text/index.vue'
import searchBar from '@/components/search/search-bar.vue'
import searchExamples from '@/components/search/search-examples.vue'
import searchHistory from '@/components/search/search-history.vue'
import searchDiscoverCta from '@/components/search/search-discover-cta.vue'
import resultsList from '@/components/search/results-list.vue'
import mobilePrototypeCardHandle from '@/components/mobile-prototyping/mobile-prototype-card-handle.vue'
import quickSearch from '@/components/quick-search/quick-search.vue'
import autocompleteResultsList from '@/components/search/autocomplete-results-list.vue'

import useMapSearch from '@/composables/search/map-search'

import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const { hasAccess: searchMapMarkersAccess } = useHasAccess(
  DomainFeatureGates.search_map_markers
)

const onFocusHandler = computed(() => {
  if (isExploreEnabled.value) {
    return () => {
      router
        .push({
          name: `fish-explore-search`,
          state: { isSearch: true },
        })
        .catch(() => {})
    }
  }
  return undefined
})

// Extract main search logic from useMapSearch composable
const {
  trimmedQueryInput,
  isLocationMode,
  isLandownerMode,
  maxNumberOfResults,
  searchResults,
  groupedResults,
  autocompleteResults,
  handleSearchFocus,
  handleInputKeyup,
  handleSearchSubmitClicked,
  handleExampleSearchClick,
  handleHistoryItemClick,
  handleSearchGroupClick,
  handleAutocompleteResultSelected,
  makeSearchRequest,
  handleResultSelected,
  clearSearch,
  resetSearch,
  error,
  setError,
} = useMapSearch({
  searchId: 'control-search',
  resultsId: 'results-list',
  focusSelectors:
    '.search-examples__list__item,.search-history__list__item,.search-result-container',
  onFocus: onFocusHandler.value,
})

const searchCardCollapsed = ref(false)
const mobileSearchBar = ref<InstanceType<typeof searchBar> | null>(null)
const controlledPlaceholder = ref<string>('')

const quickSearchConfig = computed(() => getQuickSearchConfig())

const quickSearchOptions = computed(() => {
  if (!quickSearchConfig.value) {
    return []
  }
  return quickSearchConfig.value.options
})

const showQuickSearch = computed(() => {
  return (
    searchIsActive.value &&
    isLocationMode.value &&
    quickSearchOptions.value.length &&
    isQuickSearchEnabled()
  )
})

const searchDiscoverCtaEnabled = true

const searchMode = computed(() => searchServiceMode())

const defaultPlaceholder = 'Search'

const placeholder = computed(() => {
  return (
    activeLastSearchTerm.value ||
    controlledPlaceholder.value ||
    defaultPlaceholder
  )
})

const searchIsLoading = computed(() => isLoading.value)

const isSearchGroupingEnabled = computed(() => {
  return isLocationMode.value
})

const isSearchCompleted = computed(() => searchCompleted())

const queryInputWasSearched = computed(
  () =>
    lastQueryInputFetchedLocationSearch.value ||
    lastQueryInputFetchedLandownerSearch.value
)

const hasMinCharactersForResults = computed(() => {
  if (isSearchAutocompleteEnabled()) {
    return trimmedQueryInput.value.length > 2
  }

  return true
})

const queryLengthAllowsEmptyStateUI = computed(() => {
  if (isSearchAutocompleteEnabled()) {
    return true
  }

  return trimmedQueryInput.value.length === 0
})

const noResults = computed(() => {
  return (
    queryInputWasSearched.value &&
    !searchIsLoading.value &&
    !searchResults.value?.length &&
    isSearchCompleted.value &&
    hasMinCharactersForResults.value
  )
})

const chips = computed(() => {
  const names = groupedResults.value.map((group) => {
    return { label: group.name }
  })
  return names
})

const totalResults = computed(() => {
  const totalResultsByMode = {
    [SearchMode.Location]: totalLocationResults.value,
    [SearchMode.Landowner]: totalLandownerResults.value,
  }
  return totalResultsByMode[searchMode.value]
})

const showEmptyStateUI = computed(() => {
  return (
    queryLengthAllowsEmptyStateUI.value &&
    !queryInputWasSearched.value &&
    !searchResults.value?.length &&
    !autocompleteResults.value.length
  )
})

const isExamplesVisible = computed(() => {
  if (searchIsLoading.value || error.value) {
    return false
  }
  if (showEmptyStateUI.value && history.value?.length === 0) {
    return true
  }
  return false
})

const isHistoryVisible = computed(() => {
  if (searchIsLoading.value || error.value) {
    return false
  }
  // If there are no search results and there is a history to display
  if (showEmptyStateUI.value && history.value?.length) {
    return true
  }
  return false
})

const searchModes = computed(() => {
  return [
    {
      value: SearchMode.Location,
      label: 'Location',
      selected: isLocationMode.value,
    },
    {
      value: SearchMode.Landowner,
      label: 'Private Land',
      selected: isLandownerMode.value,
    },
  ]
})

const landownerSearchStateRestriction = computed(() => {
  const states = subscriptionService.getStatesArray()

  if (
    isLandownerMode.value &&
    states.length &&
    (subscriptionService.isPremium() ||
      subscriptionService.isPremiumMultiState() ||
      subscriptionService.isTrial())
  ) {
    const stateFull = states
      .map((state) => membershipNames.convertStateAbbvToName(state))
      .join(' & ')
    return `Your membership allows you to search for landowners only in ${stateFull}.`
  }

  return null
})

const isMapboxAttributionNeeded = computed(() => {
  // Check if any results are from mapbox and we need to display attribution
  return (
    searchResults.value?.find((result) => result.item.onxType === 'GEOCODE') !==
    undefined
  )
})

const totalResultsLabel = computed(() => {
  if (
    searchMapMarkersAccess.value &&
    isLandownerMode.value &&
    totalResults.value > maxNumberOfResults.value
  ) {
    return `Showing up to ${maxNumberOfResults.value} results`
  }
  return getCountWithLabel(searchResults.value?.length ?? 0, 'result')
})

const searchIsActive = computed(() => searchServiceActive())

const lastQueryInputFetchedLandownerSearch = computed(() =>
  searchLastLandownerInput()
)
const lastQueryInputFetchedLocationSearch = computed(() =>
  searchLastLocationInput()
)

const totalLocationResults = computed(() => searchTotalLocationResults())
const totalLandownerResults = computed(() => searchTotalLandownerResults())

const mobileTestbedActive = computed(() => {
  return isMobileTestbedActive()
})

// Used for testing purposes only
defineExpose({
  setError,
})

// FUNCTIONS
const handleHuntSegmentChange = (value: SearchMode) => {
  setSearchMode(value)
  setSearchOrigin(SEARCH_ORIGINS.SEARCH_BAR)
  if (isLocationMode.value) {
    marketingEventService.publish('location_search_clicked_web')
  } else {
    marketingEventService.publish('landowner_search_clicked_web')
  }
  makeSearchRequest()
}

const clearSearchData = () => {
  clearSearch()
}

const handleQuickSearchClicked = (slug: string) => {
  router.push({
    name: `fish-quick-search`,
    params: { category: slug },
  })
  resetSearch()
}

const handleSearchBarReset = () => {
  // When search bar is reset from quick search view, close the quick search card
  if (route.path.includes('quick-search')) {
    router.push({ name: `fish-map` }).catch(() => {})
    return
  }

  resetSearch()
}

const toggleMobileSearch = () => {
  if (isExploreEnabled.value) {
    router
      .push({
        name: `fish-explore`,
        state: { isSearch: true },
      })
      .catch(() => {})
  } else {
    handleSearchFocus()

    // Focus input on delay for effect
    setTimeout(() => {
      mobileSearchBar.value?.autoFocus()
    }, 100)
  }
}

const toggleSearchCardHeight = () => {
  searchCardCollapsed.value = !searchCardCollapsed.value
}

onMounted(() => {
  pubsub.subscribe(
    'search-input-controlled-placeholder',
    (inputCopy: string) => {
      controlledPlaceholder.value = inputCopy ?? ''
    }
  )
})
</script>

<style lang="scss" scoped>
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_shadow.scss';
@use '@/styles/_layer-map.scss';
// Variables is only required by the x-segment-control component
@import '@/styles/_variables.scss';

$spacing-20: 20px;

.control-search {
  position: relative;
  z-index: layer-map.$z-index-level-controls;
  margin: 0 10px;
  user-select: none;
  pointer-events: all;
  display: inline-block;
  width: var(--card-width);

  .mobile-search-button {
    display: none;
  }

  @include breakpoints.mobile {
    width: calc(100vw - 140px);
  }

  .search-bar {
    box-shadow: shadow.$Low;
  }

  .results-panel {
    position: relative;
    top: 10px;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 95px);
    background-color: var(--ys-color-background-primary);
    margin-left: 0px;
    padding: $spacing-20 $spacing-20 $spacing-20 0;
    border-radius: 10px;
    box-shadow: shadow.$Medium;

    @include breakpoints.mobile() {
      position: absolute;
      top: 75px;
      left: -35px;
      width: calc(100vw - 40px);
      max-height: calc(100vh - 125px);
    }

    .results-list {
      flex-grow: 1;
      overflow-y: auto;
      margin-right: -$spacing-20;
      margin-top: -10px;
    }

    .segmented-control {
      border-bottom: none;
      margin-bottom: 0px;
      margin-top: 0px;
      padding-bottom: 0;
      padding-left: $spacing-20;
      padding-right: $spacing-20;
    }

    .search-filters {
      padding: $spacing-20;
    }

    .result-error {
      padding: 20px 0 0 20px;
    }

    .state-restriction {
      padding: 5px $spacing-20;
      font-size: 13px;
      color: var(--ys-color-text-dim);
      text-align: center;
      margin-right: 0;
    }

    .search-results-chip-menu {
      margin: 20px 0px 10px;
    }

    .loading-spinner {
      margin-top: $spacing-20;
    }

    .results-count,
    .no-results {
      margin-top: $spacing-20;
      color: var(--ys-color-text-dim);
      padding-left: $spacing-20;
    }
  }

  .footer {
    padding-left: $spacing-20;
    flex-shrink: 0;
    .mapbox-attribution {
      display: block;
      margin-top: 10px;
      a {
        color: var(--ys-color-text-primary);
      }

      .mapbox-icon {
        :deep(svg) {
          fill: var(--ys-color-icon-primary);
          vertical-align: top;
        }
      }
    }
  }

  &.multi-activity-experience {
    .search-bar {
      @include breakpoints.desktop() {
        :deep(.search-bar__submit) {
          margin-right: var(--ys-spacing-150);
          padding-left: 16px;
          border-right: 1px solid var(--ys-color-divider-base);
        }

        :deep(.search-bar__input::placeholder) {
          font-style: italic;
        }
      }
    }
  }

  @include breakpoints.app-prototyping {
    width: 100%;
    margin: 0 var(--ys-spacing-50) var(--ys-spacing-150);

    .desktop-search-bar {
      display: none;
    }

    .mobile-search-button {
      display: block;
      position: absolute;
      right: -20px;
      appearance: none;
      background: none;
    }

    .results-panel {
      --mobile-search-top: 40px;
      position: fixed;
      top: var(--mobile-search-top);
      left: 0;
      width: 100vw;
      max-height: none;
      height: calc(100vh - var(--mobile-search-top));
      padding-top: 0;
      background-color: var(--ys-color-surface-reverse);
      color: var(--ys-color-text-reverse);
      transform: translate3d(0, 0, 0);
      transition: transform 0.25s ease-in-out;

      .search-results {
        position: relative;
      }

      .search-results-chip-menu {
        position: sticky;
        top: 0;
        margin-top: 0;
      }

      &.searchCardCollapsed {
        transform: translate3d(0, calc(100% - 250px), 0);
      }

      .mobile-search-header {
        position: sticky;
        top: 0;
        padding: 30px 20px 20px;
        background-color: var(--ys-color-surface-reverse);
        color: var(--ys-color-text-reverse);
        z-index: 1;
      }

      .mobile-search-bar-container {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .mobile-search-bar {
        width: 100%;
      }

      :deep(.search-history__list) {
        border-color: var(--ys-color-text-dim);

        li {
          border-color: var(--ys-color-text-dim);
        }
      }

      :deep(.search-bar) {
        background-color: var(--ys-color-text-tertiary);
        color: var(--ys-color-text-reverse);
        border-radius: 24px;

        .search-bar__submit {
          opacity: 1;
        }

        .x-icon path {
          fill: var(--ys-color-text-reverse);
        }

        input {
          color: var(--ys-color-text-reverse);

          &::placeholder {
            color: var(--ys-color-text-disabled);
          }
        }
      }

      :deep(.x-segment-control) {
        padding-left: 0;
        padding-right: 0;

        .controller {
          flex-direction: row;
          background-color: var(--ys-color-controls-default);
          border-radius: 8px;

          .segment-input:checked + .segment-label {
            background-color: var(--ys-color-text-dim);
            border-radius: 6px;
          }

          .segment-label {
            color: var(--ys-color-text-reverse);
            font-weight: 400;
            padding: 6px 12px;
          }
        }
      }

      :deep(.search-result) {
        color: var(--ys-color-text-reverse);

        .result-details-subtext {
          color: var(--ys-color-text-disabled);
        }
      }

      :deep(.search-result-container) {
        border-color: var(--ys-color-text-dim);
      }

      :deep(.x-chip-menu) {
        .chip-container {
          li {
            color: var(--ys-color-text-disabled);
            border-color: var(--ys-color-text-disabled);

            &.selected-chip {
              background-color: var(--ys-color-surface-primary);
              border-color: var(--ys-color-surface-primary);
              color: var(--ys-color-text-primary);
            }
          }
        }

        .scroll-container {
          .all-content {
            &:before {
              background-image: linear-gradient(
                to left,
                rgba(255, 255, 255, 0),
                var(--ys-color-surface-reverse) 70%
              );
            }

            &:after {
              background-image: linear-gradient(
                to right,
                rgba(255, 255, 255, 0),
                var(--ys-color-surface-reverse) 70%
              );
            }
          }
        }

        .arrow {
          color: var(--ys-color-text-reverse);
        }
      }

      .discover-cta-wrapper {
        border-color: var(--ys-color-text-dim);
      }

      :deep(.search-discover-cta) {
        .cta-description {
          color: var(--ys-color-text-reverse);
        }
      }
    }

    &.explore-card {
      .desktop-search-bar {
        display: flex;
      }

      .mobile-search-button {
        display: none;
      }

      .search-bar {
        border-radius: 100px;

        :deep(.search-bar__submit) {
          margin-right: var(--ys-spacing-150);
          padding-left: var(--ys-spacing-200);
          border-right: 1px solid var(--ys-color-divider-base);
        }

        :deep(.search-bar__input::placeholder) {
          font-style: italic;
        }
      }
    }
  }
}

.discover-cta-wrapper {
  border-top: 1px solid var(--ys-color-divider-base);
  padding: var(--ys-spacing-250) var(--ys-spacing-250) 0 var(--ys-spacing-250);
}

.no-results-cta {
  padding: 0 var(--ys-spacing-200);
  margin-top: var(--ys-spacing-100);
}

.mobile-search-bar-container {
  display: none;
}

@media print {
  .control-search {
    display: none;
  }
}
</style>
